import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import useModuleAccess from '../hooks/useModuleAccess'
const API_URL = process.env.REACT_APP_API_URL

/** Resolve base64 xlsx string from export API (direct, API Gateway body, or nested JSON). */
function extractExcelBase64(raw) {
  if (raw == null) return null
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (t.startsWith('UEs')) return raw
    if (t.startsWith('{')) {
      try {
        return extractExcelBase64(JSON.parse(t))
      } catch {
        return null
      }
    }
    return raw
  }
  if (typeof raw === 'object') {
    if (raw.body != null) return extractExcelBase64(raw.body)
    if (typeof raw.data === 'string') return extractExcelBase64(raw.data)
  }
  return null
}

/** Normalize API / API Gateway body to a plain object (same pattern as attachment downloads). */
function unwrapPriceApiPayload(raw) {
  let data = raw
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return raw
    }
  }
  if (data && data.body != null) {
    const body = data.body
    if (typeof body === 'string') {
      try {
        return JSON.parse(body)
      } catch {
        return body
      }
    }
    return body
  }
  return data
}

/** Shift YYYY-MM-DD by whole days in local calendar (for export range limits). */
function shiftISODate(isoStr, deltaDays) {
  if (!isoStr) return ''
  const [y, m, d] = isoStr.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  dt.setDate(dt.getDate() + deltaDays)
  const yy = dt.getFullYear()
  const mm = String(dt.getMonth() + 1).padStart(2, '0')
  const dd = String(dt.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/** Inclusive day count between two YYYY-MM-DD strings. */
function daysInclusiveBetween(fromStr, toStr) {
  const [y1, m1, d1] = fromStr.split('-').map(Number)
  const [y2, m2, d2] = toStr.split('-').map(Number)
  const a = new Date(y1, m1 - 1, d1)
  const b = new Date(y2, m2 - 1, d2)
  return Math.round((b - a) / 86400000) + 1
}

const MAX_EXPORT_DATE_RANGE_DAYS = 90

/** Format date as DD/MM/YYYY (e.g. 04/12/2025). */
function formatTableDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`
  }
  const dmyMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (dmyMatch) {
    return `${dmyMatch[1]}/${dmyMatch[2]}/${dmyMatch[3]}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

const PriceTracker = () => {
  const { canCreate, canView, canUpdate, canDelete } =
    useModuleAccess('price_tracker')
  const savedFilters = JSON.parse(
    sessionStorage.getItem('priceTrackerFilters') || '{}'
  )

  const [prices, setPrices] = useState([])
  const [filteredPrices, setFilteredPrices] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [downloadFilesById, setDownloadFilesById] = useState({})
  const [isLoadingDownloadsById, setIsLoadingDownloadsById] = useState({})
  const [productImageUrlByPriceId, setProductImageUrlByPriceId] = useState({})
  const [isLoadingProductImageById, setIsLoadingProductImageById] = useState({})
  const [deletePriceId, setDeletePriceId] = useState(null)
  const [deletePriceRef, setDeletePriceRef] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportMode, setExportMode] = useState(savedFilters.exportMode || 'all')
  const [exportFromDate, setExportFromDate] = useState(
    savedFilters.exportFromDate || ''
  )
  const [exportToDate, setExportToDate] = useState(savedFilters.exportToDate || '')
  /** Full remarks text when modal is open; null when closed */
  const [remarksModalText, setRemarksModalText] = useState(null)

  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery || '')
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || ''
  )

  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1)
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25
  )

  const initialCreatedByUserId =
    savedFilters.createdByUserId != null && savedFilters.createdByUserId !== ''
      ? savedFilters.createdByUserId
      : savedFilters.activeFilterType === 'user' && savedFilters.activeFilterValue
        ? savedFilters.activeFilterValue
        : ''

  const [createdByUserId, setCreatedByUserId] = useState(initialCreatedByUserId)
  const [priceUsers, setPriceUsers] = useState([])
  const [isPriceUsersLoading, setIsPriceUsersLoading] = useState(false)
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const userRole = localStorage.getItem('role');

  const fetchPrices = useCallback(async () => {
    setIsLoading(true)

    try {
      const payload = {
        action: 'get-prices',
        page: currentPage,
        limit: itemsPerPage, 
        sortBy,
        sortOrder
      }

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : ''
      if (trimmedSearch) {
        payload.search = trimmedSearch
      }

      if (createdByUserId) {
        payload.createdBy = createdByUserId
      }

      const response = await axios.post(API_URL, payload)

      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      // Handle API Gateway response shape when present
      if (responseData && responseData.body) {
        if (typeof responseData.body === 'string') {
          try {
            responseData = JSON.parse(responseData.body)
          } catch (e) {
            responseData = responseData.body
          }
        } else {
          responseData = responseData.body
        }
      }

      const apiPrices = responseData?.prices || []

      if (responseData?.success === true && Array.isArray(apiPrices)) {
        setPrices(apiPrices)
        setFilteredPrices(apiPrices)

        const total = responseData?.pagination?.totalCount
        setTotalCount(typeof total === 'number' ? total : apiPrices.length)
      } else {
        toast.error(responseData?.message || 'Failed to fetch price entries', {
          autoClose: 2000
        })
        setPrices([])
        setFilteredPrices([])
        setTotalCount(0)
      }
    } catch (error) {
      console.error('Fetch prices error:', error)
      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // ignore
          }
        }
        toast.error(
          errorData?.message ||
            'An error occurred while fetching price entries',
          { autoClose: 2000 }
        )
      } else {
        toast.error('Network error. Please check your connection and try again.', {
          autoClose: 2000
        })
      }
      setPrices([])
      setFilteredPrices([])
      setTotalCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    createdByUserId,
      sortBy,
  sortOrder
  ])

  useEffect(() => {
    fetchPrices()
  }, [fetchPrices])

const handleSort = (field) => {
  if (sortBy === field) {

    if (sortOrder === 'asc') {
      setSortOrder('desc');
    } 
    else {
      setSortBy('createdAt');
      setSortOrder('desc');
    }

  } 
  else {

    setSortBy(field);
    setSortOrder('asc');

  }

  setCurrentPage(1);
};

  useEffect(() => {
    const fetchPriceUsers = async () => {
      setIsPriceUsersLoading(true)
      try {
        const payload = { action: 'get-price-users' }
        const response = await axios.post(API_URL, payload)
        let responseData = response.data
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
          } catch (e) {
            responseData = response.data
          }
        }
        if (responseData && responseData.body) {
          if (typeof responseData.body === 'string') {
            try {
              responseData = JSON.parse(responseData.body)
            } catch (e) {
              responseData = responseData.body
            }
          } else {
            responseData = responseData.body
          }
        }

        if (responseData?.success === true && Array.isArray(responseData.data)) {
          setPriceUsers(responseData.data)
        } else {
          setPriceUsers([])
        }
      } catch (error) {
        console.error('Fetch price users error:', error)
        setPriceUsers([])
      } finally {
        setIsPriceUsersLoading(false)
      }
    }

    fetchPriceUsers()
  }, [])

  // Persist filters
  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
      createdByUserId,
      exportMode,
      exportFromDate,
      exportToDate
    }
    sessionStorage.setItem('priceTrackerFilters', JSON.stringify(filters))
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    createdByUserId,
    exportMode,
    exportFromDate,
    exportToDate
  ])

 const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1)
  const startItem = prices.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const endItem = Math.min(currentPage * itemsPerPage, totalCount)

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10)
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

const handlePageChange = (page) => {
  if (page >= 1 && page <= totalPages) {
    setCurrentPage(page);

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }, 100);
  }
};

  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 7

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i)
        pages.push('ellipsis')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 3) {
        pages.push(1)
        pages.push('ellipsis')
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('ellipsis')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
        pages.push('ellipsis')
        pages.push(totalPages)
      }
    }
    return pages
  }

  const handleSearchChange = (e) => {
    const newValue = e.target.value
    setSearchQuery(newValue)

    const trimmedValue = newValue ? newValue.trim() : ''
    if (trimmedValue === '' && activeSearchQuery !== '') {
      setActiveSearchQuery('')
      setCurrentPage(1)
    }
  }

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : ''
    setActiveSearchQuery(trimmedQuery)
    setCurrentPage(1)
  }

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchTrigger()
    }
  }

  const handleCreatedByUserChange = (e) => {
    setCreatedByUserId(e.target.value)
    setCurrentPage(1)
  }

  const handleClearFilter = () => {
    setCreatedByUserId('')
    setCurrentPage(1)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString()
  }

  useEffect(() => {
    if (remarksModalText == null || !window.$) return
    const $m = window.$('#modalRemarksPrice')
    if (!$m.length) return
    $m.modal('show')
  }, [remarksModalText])

  useEffect(() => {
    if (!window.$) return
    const $m = window.$('#modalRemarksPrice')
    if (!$m.length) return
    const onHidden = () => setRemarksModalText(null)
    $m.on('hidden.bs.modal.remarks', onHidden)
    return () => $m.off('hidden.bs.modal.remarks', onHidden)
  }, [])

  const fetchDownloadFilesForId = async (priceId) => {
    const payload = {
      action: 'get-price-download-presigned-url',
      id: priceId
    }

    const response = await axios.post(API_URL, payload)
    const responseData = unwrapPriceApiPayload(response.data)

    if (responseData?.success === true && Array.isArray(responseData.files)) {
      return responseData.files
    }

    throw new Error(responseData?.message || 'Failed to load attachments')
  }

  const fetchProductImageDownloadUrl = async (priceId) => {
    const payload = {
      action: 'get-price-product-image-download-presigned-url',
      id: priceId,
      expiresIn: 7200
    }
    const response = await axios.post(API_URL, payload)
    const responseData = unwrapPriceApiPayload(response.data)
    if (responseData?.success !== true) {
      throw new Error(responseData?.message || 'Failed to load product image')
    }
    const url =
      responseData.downloadUrl ||
      responseData.url ||
      responseData.data?.downloadUrl ||
      responseData.data?.url
    if (typeof url === 'string' && url.length > 0) {
      return url
    }
    throw new Error(responseData?.message || 'Product image link not available')
  }

  const handleOpenProductImage = async (priceId) => {
    try {
      const cached = productImageUrlByPriceId[priceId]
      if (cached) {
        window.open(cached, '_blank', 'noopener,noreferrer')
        return
      }
      setIsLoadingProductImageById((prev) => ({ ...prev, [priceId]: true }))
      const downloadUrl = await fetchProductImageDownloadUrl(priceId)
      setProductImageUrlByPriceId((prev) => ({ ...prev, [priceId]: downloadUrl }))
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Open product image error:', error)
      toast.error(
        error?.message || 'Failed to open product image. Please try again.',
        { autoClose: 2000 }
      )
    } finally {
      setIsLoadingProductImageById((prev) => ({ ...prev, [priceId]: false }))
    }
  }

  const handleOpenAttachment = async (priceId, fileIndex) => {
    try {
      const existing = downloadFilesById[priceId]
      if (Array.isArray(existing) && existing[fileIndex]?.downloadUrl) {
        window.open(existing[fileIndex].downloadUrl, '_blank', 'noopener,noreferrer')
        return
      }

      setIsLoadingDownloadsById((prev) => ({ ...prev, [priceId]: true }))
      const files = await fetchDownloadFilesForId(priceId)
      setDownloadFilesById((prev) => ({ ...prev, [priceId]: files }))

      if (files[fileIndex]?.downloadUrl) {
        window.open(files[fileIndex].downloadUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('File link not available', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Open attachment error:', error)
      toast.error('Failed to open attachment. Please try again.', { autoClose: 2000 })
    } finally {
      setIsLoadingDownloadsById((prev) => ({ ...prev, [priceId]: false }))
    }
  }

  const handleDeleteClick = (priceId, label) => {
    setDeletePriceId(priceId)
    setDeletePriceRef(label || '')
  }

  const handleDelete = async () => {
    if (!deletePriceId) return

    if (!canDelete) {
      toast.error('Access denied. You do not have delete permission.', { autoClose: 2000 })
      return
    }

    const currentUserIdForDelete = localStorage.getItem('id') || ''
    if (!currentUserIdForDelete) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsDeleting(true)
    try {
      const payload = {
        action: 'delete-price',
        id: deletePriceId,
        currentUserId: currentUserIdForDelete
      }

      const response = await axios.post(API_URL, payload)
      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (e) {
          responseData = response.data
        }
      }
      if (responseData && responseData.body) {
        if (typeof responseData.body === 'string') {
          try {
            responseData = JSON.parse(responseData.body)
          } catch (e) {
            responseData = responseData.body
          }
        } else {
          responseData = responseData.body
        }
      }

      if (responseData?.success === true) {
        toast.success('Price deleted successfully', {
          autoClose: 2000
        })

        if (window.$) {
          window.$('#modaldeletePrice').modal('hide')
        }

        // Optimistic remove from current table
        setPrices((prev) => prev.filter((p) => p.id !== deletePriceId))
        setFilteredPrices((prev) => prev.filter((p) => p.id !== deletePriceId))
        setTotalCount((prev) => (prev > 0 ? prev - 1 : 0))

        // If we removed the last row on a non-first page, go back a page
        if (prices.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1)
        } else {
          fetchPrices()
        }

        setDeletePriceId(null)
        setDeletePriceRef('')
      } else {
        toast.error(responseData?.message || 'Failed to delete price', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Delete price error:', error)
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred while deleting price'
      toast.error(msg, { autoClose: 2000 })
    } finally {
      setIsDeleting(false)
    }
  }
  

const getSortIcon = (field) => {

  if (sortBy !== field) {
    return (
      <i
        className="fa-solid fa-sort text-muted ml-1"
        style={{ fontSize: '0.8em' }}
      />
    );
  }

  return sortOrder === 'asc' ? (
    <i
      className="fa-solid fa-sort-up text-primary ml-1"
      style={{ fontSize: '0.8em' }}
    />
  ) : (
    <i
      className="fa-solid fa-sort-down text-primary ml-1"
      style={{ fontSize: '0.8em' }}
    />
  );
};

  const handleExportFromDateChange = (e) => {
    const v = e.target.value
    setExportFromDate(v)
    if (!v) return
    if (exportToDate) {
      if (exportToDate < v) {
        setExportToDate(v)
      } else {
        const maxTo = shiftISODate(v, MAX_EXPORT_DATE_RANGE_DAYS - 1)
        if (exportToDate > maxTo) setExportToDate(maxTo)
      }
    }
  }

  const handleExportToDateChange = (e) => {
    const v = e.target.value
    setExportToDate(v)
    if (!v) return
    if (exportFromDate) {
      if (v < exportFromDate) {
        setExportFromDate(v)
      } else {
        const minFrom = shiftISODate(v, -(MAX_EXPORT_DATE_RANGE_DAYS - 1))
        if (exportFromDate < minFrom) setExportFromDate(minFrom)
      }
    }
  }

  const handleExport = async () => {
    if (exportMode === 'date') {
      if (!exportFromDate || !exportToDate) {
        toast.error('Please select both from date and to date', { autoClose: 2000 })
        return
      }
      if (new Date(exportFromDate) > new Date(exportToDate)) {
        toast.error('From date cannot be greater than to date', { autoClose: 2000 })
        return
      }
      const span = daysInclusiveBetween(exportFromDate, exportToDate)
      if (span > MAX_EXPORT_DATE_RANGE_DAYS) {
        toast.error(
          `Date range cannot exceed ${MAX_EXPORT_DATE_RANGE_DAYS} days`,
          { autoClose: 2500 }
        )
        return
      }
    }

    setIsExporting(true)
    try {
      const payload = {
        action: 'export-prices'
      }

      if (exportMode === 'date') {
        payload.fromDate = exportFromDate
        payload.toDate = exportToDate
      }

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : ''
      if (trimmedSearch) {
        payload.search = trimmedSearch
      }

      if (createdByUserId) {
        payload.createdBy = createdByUserId
      }

      const response = await axios.post(API_URL, payload, {
        responseType: 'json'
      })

      let base64 = extractExcelBase64(response.data)

      if (!base64 || typeof base64 !== 'string') {
        throw new Error('Invalid export response')
      }

      base64 = base64.trim()
      if (!base64.startsWith('UEs')) {
        throw new Error('Invalid Excel data received')
      }

      const byteCharacters = atob(base64)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)

      // Use server-generated .xlsx as-is. Client-side rewriting (e.g. ExcelJS) often
      // produces sheet XML that Excel repairs on open — see sheet1.xml load errors.
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
      link.download = `price-export-${timestamp}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Export completed successfully', { autoClose: 2000 })
      setShowExportModal(false)
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export data. Please try again.', { autoClose: 2000 })
    } finally {
      setIsExporting(false)
    }
  }

  const showClearButton = Boolean(createdByUserId)

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Price List</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <Link to="/priceTrackerAdd" className="btn btn-blue">
                  Add
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card-body">
                <div className="row align-items-end mb-3">
                  <div className="col-md-4 col-lg-3">
                    <div className="form-group mb-0">
                      <label
                        className="mb-1 text-muted d-block"
                        style={{ fontSize: '12px', fontWeight: '500' }}
                      >
                        Search
                      </label>
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search price entries..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                          style={{ fontSize: '12px' }}
                        />
                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr py-0 px-2"
                            onClick={handleSearchTrigger}
                            style={{ cursor: 'pointer', fontSize: '12px' }}
                          >
                            <i className="fa-solid fa-search"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 col-lg-3">
                    <div className="form-group mb-0">
                      <label
                        className="mb-1 text-muted d-block"
                        style={{ fontSize: '12px', fontWeight: '500' }}
                      >
                        Created by
                      </label>
                      <select
                        className="form-control form-control-sm"
                        value={createdByUserId}
                        onChange={handleCreatedByUserChange}
                        disabled={isPriceUsersLoading}
                        style={{ fontSize: '12px' }}
                      >
                        <option value="">
                          {isPriceUsersLoading ? 'Loading users...' : 'All users'}
                        </option>
                        {priceUsers.map((u) => (
                          <option key={u._id} value={u._id}>
                            {u?.name || u._id}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4 col-lg-6 text-md-right mt-2 mt-md-0">
                    <div className="form-group mb-0">
                      <label
                        className="mb-1 d-block text-md-right"
                        style={{ fontSize: '12px', visibility: 'hidden' }}
                        aria-hidden="true"
                      >
                        &nbsp;
                      </label>
                      <div
                        className="d-flex flex-wrap justify-content-md-end justify-content-start align-items-center"
                        style={{ gap: '8px' }}
                      >
                        {showClearButton && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={handleClearFilter}
                            title="Clear filter"
                          >
                            <i className="fa-solid fa-times mr-1"></i>
                            Clear Filter
                          </button>
                        )}
                        {userRole === "admin" && (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={() => setShowExportModal(true)}
                            disabled={isExporting}
                          >
                            <i
                              className={`fa-solid ${
                                isExporting
                                  ? "fa-spinner fa-spin"
                                  : "fa-file-export"
                              } mr-1`}
                            />
                            {isExporting ? "Exporting..." : "Export"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : prices.length === 0 ? (
                <div className="nocontent_div text-center p-4">
                  There are no price entries yet.
                </div>
              ) : filteredPrices.length === 0 && activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  No price entries found matching your search.
                  <div className="mt-3">
                    <button
                      className="btn btn-blue"
                      onClick={() => {
                        setSearchQuery('')
                        setActiveSearchQuery('')
                        setCurrentPage(1)
                      }}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : filteredPrices.length === 0 && createdByUserId ? (
                <div className="nocontent_div text-center p-4">
                  No price entries found matching your filter.
                  <div className="mt-3">
                    <button
                      className="btn btn-blue"
                      onClick={handleClearFilter}
                    >
                      Clear Filter
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="prices"
                      className="table table-striped table-bordered txt_lft mb-0"
                      style={{ minWidth: '1560px', width: '100%', tableLayout: 'fixed' }}
                    >
                      <colgroup>
                        <col style={{ width: '44px' }} />
                        <col style={{ width: '168px' }} />
                        <col style={{ width: '56px' }} />
                        <col style={{ width: '92px' }} />
                        <col style={{ width: '56px' }} />
                        <col style={{ width: '92px' }} />
                        <col style={{ width: '82px' }} />
                        <col style={{ width: '82px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '168px' }} />
                        <col style={{ width: '82px' }} />
                        <col style={{ width: '112px' }} />
                        <col style={{ width: '88px' }} />
                        <col style={{ width: '108px' }} />
                      </colgroup>
                      <thead>
                        <tr style={{ fontSize: '11px' }}>
                          <th
                            rowSpan={2}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            S.No
                          </th>
                          <th 
                            rowSpan={2}
                              onClick={() => handleSort('productName')}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Product Name{getSortIcon('productName')}
                          </th>
                          <th
                            colSpan={2}
                            style={{
                              fontSize: '11px',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.55)',
                              fontWeight: 600
                            }}
                          >
                            Purchase
                          </th>
                          <th
                            colSpan={2}
                            style={{
                              fontSize: '11px',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.55)',
                              fontWeight: 600
                            }}
                          >
                            Sales
                          </th>
                          <th                          
                            onClick={() => handleSort('priceValidFrom')}
                            rowSpan={2}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Valid From {getSortIcon('priceValidFrom')}
                          </th>
                          <th
                           onClick={() => handleSort('priceValidTill')}
                            rowSpan={2}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap'
                            }}
                          >
                             Valid Till {getSortIcon('priceValidTill')}
                          </th>
                          <th
                            rowSpan={2}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Remarks
                          </th>
                          <th
                           onClick={() => handleSort('supplierName')}
                            rowSpan={2}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              width: '168px',
                              maxWidth: '168px',
                              boxSizing: 'border-box',
                              overflow: 'hidden'
                            }}
                          >
                             Supplier Reference {getSortIcon('supplierName')}
                          </th>
                          <th
                            onClick={() => handleSort('createdAt')}
                            rowSpan={2}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Created At {getSortIcon('createdAt')}
                          </th>
                          <th
                           onClick={() => handleSort('updatedBy')}
                            rowSpan={2}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap'
                            }}
                          >
                           Last Updated By {getSortIcon('updatedBy')}
                          </th>
                          <th
                            rowSpan={2}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Attachments
                          </th>
                          <th
                            rowSpan={2}
                            style={{
                              fontSize: '11px',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap',
                              minWidth: '108px'
                            }}
                          >
                            Action
                          </th>
                        </tr>
                        <tr style={{ fontSize: '11px' }}>
                          <th 
                            style={{
                              fontSize: '11px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              fontWeight: 600
                            }}
                          >
                            UOM 
                          </th>
                          <th 
                            style={{
                              fontSize: '11px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              fontWeight: 600
                            }}
                          >
                              Price/Unit 

                          </th>
                          <th   
                            style={{
                              fontSize: '11px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              fontWeight: 600
                            }}
                          >
                            UOM 
                          </th>
                          <th  
                            style={{
                              fontSize: '11px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              fontWeight: 600
                            }}
                          >
                             Price/Unit 
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPrices.map((entry, index) => {
                          const supplierNameRaw = entry.supplierName
                          const supplierName = Array.isArray(supplierNameRaw)
                            ? supplierNameRaw.filter(Boolean).join(', ')
                            : supplierNameRaw || '-'
                          const productName = entry.productName || '-'
                          const purchaseUom =
                            entry.purchaseUom != null && String(entry.purchaseUom).trim() !== ''
                              ? String(entry.purchaseUom).trim()
                              : '-'
                          const salesUom =
                            entry.salesUom != null && String(entry.salesUom).trim() !== ''
                              ? String(entry.salesUom).trim()
                              : '-'
                          const purchasePricePerUnit = entry.purchasePricePerUnit
                          const salesPricePerUnit = entry.salesPricePerUnit
                          const priceValidFrom = entry.priceValidFrom
                          const priceValidTill = entry.priceValidTill
                          const remarksFull =
                            entry.remarks != null && String(entry.remarks).trim() !== ''
                              ? String(entry.remarks).trim()
                              : null
                          const lastUpdatedBy = entry.updatedByName || entry.createdByName || '-'
                          const statusRaw = (entry.status || '')
                            .toString()
                            .toLowerCase()
                            .trim()
                          let statusLabel = '-'
                          const statusPillBase = {
                            fontSize: '10px',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            color: '#fff',
                            fontWeight: 600,
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap'
                          }
                          let statusPillStyle = {
                            ...statusPillBase,
                            backgroundColor: '#6c757d'
                          }
                          if (statusRaw === 'active') {
                            statusLabel = 'Active'
                            statusPillStyle = { ...statusPillBase, backgroundColor: '#28a745' }
                          } else if (statusRaw === 'draft') {
                            statusLabel = 'Draft'
                            statusPillStyle = { ...statusPillBase, backgroundColor: '#fd7e14' }
                          } else if (entry.status) {
                            statusLabel = String(entry.status)
                            statusPillStyle = { ...statusPillBase, backgroundColor: '#17a2b8' }
                          }
                          const attachmentCount = Array.isArray(entry.attachments)
                            ? entry.attachments.length
                            : 0
                          const productImageKey =
                            entry.productImage &&
                            typeof entry.productImage === 'object' &&
                            entry.productImage.s3Key
                          const isLoadingDownloads = Boolean(isLoadingDownloadsById[entry.id])
                          const isLoadingProductImage = Boolean(
                            isLoadingProductImageById[entry.id]
                          )

                          return (
                            <tr
                              key={entry.id || index}
                              style={{ fontSize: '12px', verticalAlign: 'top' }}
                            >
                              <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td
                                style={{
                                  fontSize: '12px',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  maxWidth: '220px'
                                }}
                              >
                                {productImageKey ? (
                                  <span className="d-inline-flex align-items-center flex-wrap">
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-left"
                                      style={{
                                        fontSize: '12px',
                                        verticalAlign: 'baseline'
                                      }}
                                      onClick={() => handleOpenProductImage(entry.id)}
                                      disabled={isLoadingProductImage}
                                      title="Open product image"
                                    >
                                      {productName}
                                    </button>
                                    {isLoadingProductImage && (
                                      <span
                                        className="text-muted ml-1"
                                        style={{ fontSize: '11px' }}
                                      >
                                        Loading...
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  productName
                                )}
                              </td>
                              <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {purchaseUom}
                              </td>
                              <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {purchasePricePerUnit != null &&
                                !Number.isNaN(Number(purchasePricePerUnit))
                                  ? Number(purchasePricePerUnit).toLocaleString('en-IN')
                                  : '-'}
                              </td>
                              <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {salesUom}
                              </td>
                              <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {salesPricePerUnit != null &&
                                !Number.isNaN(Number(salesPricePerUnit))
                                  ? Number(salesPricePerUnit).toLocaleString('en-IN')
                                  : '-'}
                              </td>
                              <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {formatDate(priceValidFrom)}
                              </td>
                              <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {formatDate(priceValidTill)}
                              </td>
                              <td
                                style={{
                                  fontSize: '12px',
                                  verticalAlign: 'top',
                                  maxWidth: '140px'
                                }}
                              >
                                {remarksFull == null ? (
                                  '-'
                                ) : (
                                  <button
                                    type="button"
                                    className="btn p-0 border-0 bg-transparent text-left w-100"
                                    style={{
                                      cursor: 'pointer',
                                      color: 'inherit',
                                      maxWidth: '140px'
                                    }}
                                    onClick={() => setRemarksModalText(remarksFull)}
                                    title="View full remarks"
                                  >
                                    <span
                                      style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                        fontSize: '12px',
                                        lineHeight: 1.35,
                                        textAlign: 'left'
                                      }}
                                    >
                                      {remarksFull}
                                    </span>
                                  </button>
                                )}
                              </td>
                              <td
                                title={typeof supplierName === 'string' ? supplierName : ''}
                                style={{
                                  fontSize: '12px',
                                  verticalAlign: 'top',
                                  width: '150px',
                                  maxWidth: '150px',
                                  minWidth: '150px',
                                  boxSizing: 'border-box'
                                }}
                              >
                                <div
                                  style={{
                                    maxWidth: '150px',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal'
                                  }}
                                >
                                  {supplierName}
                                </div>
                              </td>
                              <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {formatTableDate(entry.createdAt)}
                              </td>
                              <td
                                style={{
                                  fontSize: '12px',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  minWidth: '120px'
                                }}
                              >
                                {lastUpdatedBy}
                              </td>
                              <td style={{ fontSize: '12px' }}>
                                {attachmentCount > 0 ? (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {Array.from({ length: attachmentCount }).map((_, fileIdx) => (
                                      <button
                                        key={`${entry.id}-file-${fileIdx}`}
                                        type="button"
                                        className="btn btn-link p-0"
                                        style={{ fontSize: '12px' }}
                                        onClick={() => handleOpenAttachment(entry.id, fileIdx)}
                                        disabled={isLoadingDownloads}
                                        title="Open attachment"
                                      >
                                        File {fileIdx + 1}
                                      </button>
                                    ))}
                                    {isLoadingDownloads && (
                                      <span className="text-muted" style={{ fontSize: '12px' }}>
                                        Loading...
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td style={{ fontSize: '13px', textAlign: 'center' }}>
                                <div className="d-flex flex-column align-items-center justify-content-center">
                                  <span style={statusPillStyle} className="mb-1">
                                    {statusLabel}
                                  </span>
                                  <div className="d-flex justify-content-center align-items-center flex-wrap">
                                    {canView && (
                                      <Link
                                        to={`/priceTrackerView/${entry.id}`}
                                        className="crsr_pntr mr-2"
                                        title="View"
                                      >
                                        <i className="fa-solid fa-eye" />
                                      </Link>
                                    )}
                                    {canUpdate && (
                                      <Link
                                        to={`/priceTrackerEdit/${entry.id}`}
                                        className="crsr_pntr mr-2"
                                        title="Edit"
                                      >
                                        <i className="fa-solid fa-pen" />
                                      </Link>
                                    )}
                                    {canDelete && (
                                      <button
                                        type="button"
                                        className="crsr_pntr btn btn-link p-0 border-0 align-baseline mr-2"
                                        data-toggle="modal"
                                        data-target="#modaldeletePrice"
                                        onClick={() =>
                                          handleDeleteClick(
                                            entry.id,
                                            entry.productName || entry.id
                                          )
                                        }
                                        title="Delete"
                                        style={{
                                          color: 'var(--blue, #0094a9)',
                                          textDecoration: 'none'
                                        }}
                                      >
                                        <i
                                          className="fa-solid fa-trash-can"
                                          style={{ color: 'inherit' }}
                                          aria-hidden="true"
                                        />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && prices.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full remarks (from table cell clamp) */}
      <div
        className="modal fade"
        id="modalRemarksPrice"
        tabIndex={-1}
        aria-labelledby="modalRemarksPriceLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalRemarksPriceLabel">
                Remarks
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '13px',
                  lineHeight: 1.5
                }}
              >
                {remarksModalText || ''}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Price Modal (Admin only) */}
      <div
        className="modal fade"
        id="modaldeletePrice"
        tabIndex={-1}
        aria-labelledby="modaldeletePriceLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeletePriceLabel">
                Delete Price
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete <br />
                <span className="fwt_600">{deletePriceRef}</span>?
                <div className="text-muted mt-2" style={{ fontSize: '12px' }}>
                  This is a soft delete (status will be set to inactive).
                </div>
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, delete'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showExportModal ? 'show' : ''}`}
        style={{ display: showExportModal ? 'block' : 'none' }}
        tabIndex={-1}
        aria-labelledby="modalExportPriceLabel"
        aria-hidden={!showExportModal}
        onClick={() => {
          if (!isExporting) setShowExportModal(false)
        }}
      >
        <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalExportPriceLabel">
                Export Price Data
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={() => {
                  if (!isExporting) setShowExportModal(false)
                }}
                disabled={isExporting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb-3">
                <label className="mb-2 d-block">Export Type</label>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportAllPrices"
                    name="exportTypePrices"
                    className="custom-control-input"
                    value="all"
                    checked={exportMode === 'all'}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label className="custom-control-label" htmlFor="exportAllPrices">
                    All
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportDatePrices"
                    name="exportTypePrices"
                    className="custom-control-input"
                    value="date"
                    checked={exportMode === 'date'}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label className="custom-control-label" htmlFor="exportDatePrices">
                    Filter by Date
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportFromDate}
                    onChange={handleExportFromDateChange}
                    disabled={isExporting || exportMode !== 'date'}
                    min={
                      exportToDate
                        ? shiftISODate(exportToDate, -(MAX_EXPORT_DATE_RANGE_DAYS - 1))
                        : undefined
                    }
                    max={exportToDate || undefined}
                  />
                </div>
                <div className="form-group col-md-6 mb-0">
                  <label className="mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportToDate}
                    onChange={handleExportToDateChange}
                    disabled={isExporting || exportMode !== 'date'}
                    min={exportFromDate || undefined}
                    max={
                      exportFromDate
                        ? shiftISODate(exportFromDate, MAX_EXPORT_DATE_RANGE_DAYS - 1)
                        : undefined
                    }
                  />
                </div>
              </div>
              <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                When filtering by date, the range cannot exceed {MAX_EXPORT_DATE_RANGE_DAYS}{' '}
                days (inclusive).
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showExportModal && <div className="modal-backdrop fade show" />}

    </div>
  )
}

export default PriceTracker